import React, { useEffect, useRef, useState } from "react";
import { Phone, ShieldCheck, ExternalLink, ArrowUpRight, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";

const INTAKE_URL = "https://secure.foratravel.com/intake/j73lxRlF3h";

/**
 * Client Profile Intake — primary action is "Open Intake" which launches the
 * Fora Travel form in a new tab (Fora blocks <iframe> embedding from other
 * origins via X-Frame-Options, so a direct embed renders blank). We still
 * attempt the iframe in case Fora ever allows framing for our domain; if
 * after 6 seconds the iframe hasn't fired a load event we hide it gracefully.
 *
 * Below the form is a discreet "Administrative" panel — a shortcut for staff
 * to jump straight into /admin from this page.
 */
export default function ConciergePage() {
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);

  useEffect(() => {
    // 6-second timeout: if the iframe never fires `onLoad`, assume Fora
    // blocked us via CSP / X-Frame-Options and surface the open-in-tab CTA.
    const t = setTimeout(() => {
      if (!iframeLoaded) setIframeBlocked(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [iframeLoaded]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="concierge-page">
      <Phone size={28} strokeWidth={1.25} className="text-[var(--gold)]" />
      <div className="overline mt-4">Live concierge · Client profile intake</div>
      <h1 className="font-display text-5xl text-[var(--emerald)] mt-3" data-testid="concierge-headline">
        Meet <strong className="font-bold">Your</strong> Concierge.
      </h1>
      <p className="text-[var(--ink-2)] mt-3 max-w-xl">
        Tell us about the moment you're planning. Our concierge will reach out personally —
        and you'll receive a confirmation # so we can recall your trip on demand for the
        next 30 days.
      </p>

      {/* Primary CTA card — always works regardless of iframe blocking */}
      <div className="mt-10 lux-card border-t-2 border-[var(--gold)]" data-testid="intake-cta-card">
        <div className="flex items-start gap-4">
          <ClipboardCheck size={22} strokeWidth={1.5} className="text-[var(--gold)] shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="overline">Client profile intake</div>
            <h2 className="font-display text-3xl text-[var(--emerald)] mt-2 leading-tight">
              Begin your client profile.
            </h2>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
              The intake takes about 8 minutes — travel rhythms, loyalty programs,
              dietary notes, the names of the people who'll travel with you, and
              the moments that matter most. Your concierge reviews it before
              your first call.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="intake-open-btn"
              >
                Open intake form <ArrowUpRight size={14} strokeWidth={1.75} />
              </a>
              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[var(--brand-red)] hover:text-[var(--emerald)] px-3 py-2.5"
                data-testid="intake-fallback-link"
              >
                Open in a new tab <ExternalLink size={11} strokeWidth={1.75} />
              </a>
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
              Secured by foratravel.com · your responses sync into your Janté concierge record.
            </p>
          </div>
        </div>
      </div>

      {/* Best-effort iframe preview — hidden the moment we detect Fora blocks framing */}
      {!iframeBlocked && (
        <div
          className={`mt-6 lux-card !p-0 overflow-hidden transition-opacity ${
            iframeLoaded ? "opacity-100" : "opacity-50"
          }`}
          data-testid="intake-frame-card"
        >
          <iframe
            ref={iframeRef}
            src={INTAKE_URL}
            title="Janté Client Profile Intake"
            className="w-full block"
            style={{ minHeight: "1400px", border: 0 }}
            loading="lazy"
            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
            onLoad={() => setIframeLoaded(true)}
            onError={() => setIframeBlocked(true)}
            data-testid="intake-iframe"
          />
        </div>
      )}

      {/* ------ Administrative ------ */}
      <section
        className="mt-12 pt-8 border-t border-[var(--emerald)]/15"
        data-testid="concierge-admin-section"
      >
        <div className="overline">Administrative</div>
        <p className="text-sm text-[var(--ink-2)] mt-2">
          Staff access to the Janté ops dashboard — review intakes, confirm
          deposits, manage listings, and audit consents.
        </p>
        <Link
          to="/admin"
          className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border border-[var(--gold)] text-[var(--emerald)] hover:bg-[var(--emerald)] hover:text-[var(--ivory)] transition-colors"
          data-testid="concierge-admin-btn"
        >
          <ShieldCheck size={12} strokeWidth={1.5} /> Open admin dashboard
        </Link>
      </section>
    </div>
  );
}
